import {Card, Col, Container, Row} from "react-bootstrap";
import {ProductRequirements, ProductViewModel} from "../../api/models";
import {accentProps} from "../../components/accent";
import {
    allFormats, architectureName, gapLines, hasGaps, hasRequirements, isMobile, platformIcons, requirementGaps, sortedPlatforms,
    storeName
} from "../../components/requirements";
import {format, language, products} from "../../i18n";
import {Reveal} from "../showcase/Reveal";

const messages = products.requirements;
const andList = new Intl.ListFormat(language, { type: 'conjunction' });
export const REQUIREMENTS_ANCHOR = 'requirements';

// A label and its value, as a row of a definition list; nothing when there's no value.
function Item({label, value}: { label: string, value?: string }) {
    return value ? <><dt className="col-sm-4 fw-semibold">{label}</dt><dd className="col-sm-8 mb-2">{value}</dd></> : null;
}

// A product's System requirements section: a card for each platform, then what every platform needs, then what isn't
// available (worked out from the platforms, components/requirements.ts). Nothing without requirements.
export function ProductRequirementsSection({product}: { product: ProductViewModel }) {
    const requirements = product.requirements;
    if (!hasRequirements(requirements)) {
        return null;
    }
    const gaps = requirementGaps(requirements);
    const general = [requirements.processor, requirements.memory, requirements.disk, requirements.other].some(x => x?.trim());

    return (
        <section id={REQUIREMENTS_ANCHOR} className="showcase-section" {...accentProps(product.accentColor)} aria-labelledby="product-requirements-title">
            <Container>
                <h2 id="product-requirements-title" className="showcase-title">{messages.title}</h2>
                <Reveal>
                    <Row className="g-4">
                        {sortedPlatforms(requirements).map(x => {
                            const store = x.storeUrl && storeName(x.storeUrl);
                            return (
                                <Col key={x.platform} md={6} lg={4}>
                                    <Card className="showcase-card h-100">
                                        <Card.Body className="p-4">
                                            <h3 className="card-title h5 mb-3">
                                                <i className={`bi ${platformIcons[x.platform]} me-2`} aria-hidden="true"></i>{messages.platforms[x.platform]}
                                            </h3>
                                            <dl className="row mb-0">
                                                <Item label={messages.minimumVersion} value={x.minimumVersion}/>
                                                <Item label={messages.processors} value={andList.format(x.architectures.map(a => architectureName(x.platform, a)))}/>
                                                <Item label={messages.formats} value={andList.format(x.formats.map(f => messages.formatNames[f]))}/>
                                                <Item label={messages.devices} value={x.devices}/>
                                            </dl>
                                            {x.storeUrl && isMobile(x.platform) && (
                                                <a href={x.storeUrl} rel="noopener" className="btn btn-showcase-outline mt-3">
                                                    <i className="bi bi-box-arrow-up-right me-2" aria-hidden="true"></i>
                                                    {store ? format(messages.getIt, { store }) : messages.platforms[x.platform]}
                                                </a>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            );
                        })}
                        {general && (
                            <Col md={6} lg={requirements.platforms.length ? 4 : 6}>
                                <Card className="showcase-card h-100">
                                    <Card.Body className="p-4">
                                        {requirements.platforms.length > 0 && <h3 className="card-title h5 mb-3">{messages.allPlatforms}</h3>}
                                        <dl className="row mb-0">
                                            <Item label={messages.processor} value={requirements.processor}/>
                                            <Item label={messages.memory} value={requirements.memory}/>
                                            <Item label={messages.disk} value={requirements.disk}/>
                                            <Item label={messages.other} value={requirements.other}/>
                                        </dl>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                        {hasGaps(gaps) && (
                            <Col md={6} lg={4}>
                                <Card className="showcase-card h-100">
                                    <Card.Body className="p-4">
                                        <h3 className="card-title h5 mb-3">{messages.notAvailable}</h3>
                                        <dl className="row mb-0">
                                            {gapLines(gaps).map(x => <Item key={x.label} label={x.label} value={x.text}/>)}
                                        </dl>
                                        {gaps.notAvailable && <p className="mb-0 mt-1">{gaps.notAvailable}</p>}
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </Reveal>
            </Container>
        </section>
    );
}

// Beside the description, near the buy button: the platforms and formats, and a link to the section.
export function RequirementsSummary({requirements}: { requirements?: ProductRequirements | null }) {
    if (!hasRequirements(requirements)) {
        return null;
    }
    const platforms = sortedPlatforms(requirements).map(x => messages.platforms[x.platform]);
    const formats = allFormats(requirements).map(x => messages.formatShortNames[x]);
    return (
        <p className="small text-body-secondary mt-3 mb-0">
            {platforms.length > 0 && <span className="d-block">{platforms.join(' · ')}</span>}
            {formats.length > 0 && <span className="d-block">{formats.join(', ')}</span>}
            <a href={`#${REQUIREMENTS_ANCHOR}`}>{messages.summaryLink}</a>
        </p>
    );
}
